// Italian fiscal code (codice fiscale) validation.
//
// Checks format, check character and that the first six letters match surname
// and name. Used by the trip/user forms (inline warning while typing) and by the
// invoice recipient forms in the history (blocking). Twin of the backend
// `functions/utils/fiscalCode.js`, which refuses the same codes before they
// reach Effatta: keep the two aligned (same fixtures in both test suites,
// `__tests__/fixtures/fiscalCode.fixtures.json`).

export const FISCAL_CODE_ERRORS = {
  REQUIRED: 'required',
  FORMAT: 'format',
  CHECKSUM: 'checksum',
  NAMES_SWAPPED: 'names_swapped',
  SURNAME_MISMATCH: 'surname_mismatch',
  NAME_MISMATCH: 'name_mismatch',
};

// Omocodia: when two people share a code, digits are replaced by these letters
// (0 → L … 9 → V), so every digit position also accepts them.
const OMOCODE_LETTERS = 'LMNPQRSTUV';
const D = `[0-9${OMOCODE_LETTERS}]`;
const PERSONAL_CF_RE = new RegExp(`^[A-Z]{6}${D}{2}[ABCDEHLMPRST]${D}{2}[A-Z]${D}{3}[A-Z]$`);
const NUMERIC_CF_RE = /^\d{11}$/;

// Check-character tables (DM 23/12/1976): odd positions (1-indexed) use a
// scrambled table, even positions the plain value (digit, or A = 0 … Z = 25).
const ODD_VALUES = [
  1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23,
];
const charIndex = (c) => (/\d/.test(c) ? Number(c) : c.charCodeAt(0) - 65);

const personalCheckChar = (cf) => {
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const idx = charIndex(cf[i]);
    sum += i % 2 === 0 ? ODD_VALUES[idx] : idx;
  }
  return String.fromCharCode(65 + (sum % 26));
};

// Same algorithm as the partita IVA: companies' fiscal code is numeric.
const numericChecksumOk = (cf) => {
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let n = Number(cf[i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return (10 - (sum % 10)) % 10 === Number(cf[10]);
};

const decodeOmocode = (s) => s.replace(/[LMNPQRSTUV]/g, (c) => String(OMOCODE_LETTERS.indexOf(c)));

const validBirthDay = (cf) => {
  const day = Number(decodeOmocode(cf.slice(9, 11)));
  return (day >= 1 && day <= 31) || (day >= 41 && day <= 71);
};

/** Uppercase A-Z only: accents dropped, spaces and apostrophes removed. */
const normalizeName = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

const splitLetters = (s) => ({
  consonants: s.replace(/[AEIOU]/g, ''),
  vowels: s.replace(/[^AEIOU]/g, ''),
});

/** Chars 1-3: consonants, then vowels, padded with X. */
export const surnameCode = (surname) => {
  const { consonants, vowels } = splitLetters(normalizeName(surname));
  return `${consonants}${vowels}XXX`.slice(0, 3);
};

/** Chars 4-6: like the surname, but with 4+ consonants take the 1st, 3rd and 4th. */
export const nameCode = (name) => {
  const { consonants, vowels } = splitLetters(normalizeName(name));
  if (consonants.length >= 4) return consonants[0] + consonants[2] + consonants[3];
  return `${consonants}${vowels}XXX`.slice(0, 3);
};

/**
 * Checks a fiscal code: format, check character and — when given — that the
 * first six letters match surname and name. Returns the first problem found.
 *
 * A missing name or surname is not an error here (it can't be checked); the
 * caller decides whether those are required.
 *
 * @param {string} cf
 * @param {{firstName?: string, lastName?: string, allowNumeric?: boolean}} [options]
 *   allowNumeric: accept the 11-digit code of companies (no name check).
 * @returns {{valid: boolean, error: string|null, expected?: {surname: string, name: string}}}
 */
export const validateFiscalCode = (cf, { firstName, lastName, allowNumeric = false } = {}) => {
  const code = String(cf ?? '')
    .replace(/\s+/g, '')
    .toUpperCase();
  const fail = (error, extra = {}) => ({ valid: false, error, ...extra });

  if (!code) return fail(FISCAL_CODE_ERRORS.REQUIRED);

  if (NUMERIC_CF_RE.test(code)) {
    if (!allowNumeric) return fail(FISCAL_CODE_ERRORS.FORMAT);
    return numericChecksumOk(code)
      ? { valid: true, error: null }
      : fail(FISCAL_CODE_ERRORS.CHECKSUM);
  }

  if (!PERSONAL_CF_RE.test(code) || !validBirthDay(code)) return fail(FISCAL_CODE_ERRORS.FORMAT);
  if (personalCheckChar(code) !== code[15]) return fail(FISCAL_CODE_ERRORS.CHECKSUM);

  const hasSurname = !!normalizeName(lastName);
  const hasName = !!normalizeName(firstName);
  const surnameOk = !hasSurname || surnameCode(lastName) === code.slice(0, 3);
  const nameOk = !hasName || nameCode(firstName) === code.slice(3, 6);
  if (surnameOk && nameOk) return { valid: true, error: null };

  const expected = {
    surname: hasSurname ? surnameCode(lastName) : null,
    name: hasName ? nameCode(firstName) : null,
  };
  const swapped =
    hasSurname &&
    hasName &&
    surnameCode(firstName) === code.slice(0, 3) &&
    nameCode(lastName) === code.slice(3, 6);
  if (swapped) return fail(FISCAL_CODE_ERRORS.NAMES_SWAPPED, { expected });
  if (!surnameOk) return fail(FISCAL_CODE_ERRORS.SURNAME_MISMATCH, { expected });
  return fail(FISCAL_CODE_ERRORS.NAME_MISMATCH, { expected });
};
